import { useEffect, useMemo, useState } from 'react';

import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import PageShell from '#/modules/infra/layout/PageShell';
import { buildInventorySafetyCheckColumns } from '#/modules/imsMgmt/inventoryStock/data/inventorySafetyCheckColumns';
import useInventorySafetyCheckList from '#/modules/imsMgmt/inventoryStock/hooks/useInventorySafetyCheckList';
import { hydrateWarehouseFilterLabels } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { getRequestErrorMessage } from '#/utils/requestError';

/**
 * 安全庫存不足清單（唯讀）。只列出 ACTIVE＋AVAILABLE 庫存低於物料安全量的物料；
 * 已達安全量者後端不回傳。數量顯示由前端依 display metadata 換算。
 */
const InventorySafetyCheckPage = () => {
  const [warehouseLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    resetListQuery,
  } = useInventorySafetyCheckList();

  useEffect(() => {
    let cancelled = false;
    hydrateWarehouseFilterLabels(listQuery.filters?.warehouseId, warehouseLabelCache).then((did) => {
      if (!cancelled && did) {
        setLabelCacheVersion((version) => version + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [listQuery.filters?.warehouseId, warehouseLabelCache]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildInventorySafetyCheckColumns({
        filters: listQuery.filters || {},
        onFilterChange: setFilter,
        warehouseLabelCache,
      });
    },
    [listQuery.filters, setFilter, warehouseLabelCache, labelCacheVersion],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns),
    [columns],
  );

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">安全庫存</span>
        </h1>
        <p className="mb-3 shrink-0 text-sm text-gray-600">
          只列出可用庫存低於安全量的物料；可用量只計「啟用＋可用」的庫存，可在「可用量」欄指定倉別。
        </p>
        <div className="table-panel min-h-0 xl:h-full">
          <ListTablePanel
            tableKey="ims.inventory_stock.safety_check"
            columns={columns}
            rows={rows}
            pagination={pagination}
            isLoading={isLoading}
            isError={isError}
            errorMessage={isError ? getRequestErrorMessage(error, '載入安全庫存失敗') : undefined}
            onRetry={isError ? () => { refetch(); } : undefined}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onSearch={setKeyword}
            onReset={resetListQuery}
            initialKeyword={listQuery.keyword}
            searchPlaceholder="搜尋料號或名稱"
            listQuery={listQuery}
            filterStatusFields={filterStatusFields}
            onFilterChange={setFilter}
            onKeywordClear={() => setKeyword('')}
          />
        </div>
      </div>
    </PageShell>
  );
};

export default InventorySafetyCheckPage;
